import { useMutation } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api/client';

/* ───────────────────────────────────────────────────────────────────────────
   Contrato — POST /fiscal/regime-simulator (FiscalAnalyticsController)

   Espelha `RegimeSimulatorRequestDto` e `RegimeSimulatorResult`
   (application/commands/simulate-regimes.usecase.ts).

   Request body:
     - annualRevenue: number (obrigatório, > 0)
     - operationType? (VENDA|COMPRA|...)
     - originUf: string (obrigatório)
     - destinationUf: string (obrigatório)
     - items?: Array<{ ncmCode, cfopCode?, quantity, unitValue, sharePct? }>
       (padrão: mix de referência distribuído pela receita)
     - year?: number
     - simplesAnexo? (ANEXO_I..ANEXO_V; padrão ANEXO_I)

   Response: `RegimeProjection[]` (um por regime — Simples Nacional, Lucro
   Presumido, Lucro Real), com `das` (SimplesProjecao) quando Simples.

   O tenant vem da sessão.
   ─────────────────────────────────────────────────────────────────────────── */

export interface RegimeSimulationItem {
  ncmCode: string;
  cfopCode?: string;
  quantity: number;
  unitValue: number;
  sharePct?: number;
}

export interface RegimeSimulatorInput {
  annualRevenue: number;
  operationType?: string;
  originUf: string;
  destinationUf: string;
  items?: RegimeSimulationItem[];
  year?: number;
  simplesAnexo?: string;
}

export interface SimplesProjecao {
  anexo: string;
  receitaBruta12: number;
  faixa: number;
  aliquotaNominal: number;
  aliquotaEfetiva: number;
  dasAnual: number;
  reparticao: Record<string, number>;
}

export interface RegimeProjection {
  regime: string;
  label: string;
  annualRevenue: number;
  estimatedTotalTax: number;
  effectiveRatePct: number;
  breakdown: Array<{ taxType: string; base: number; value: number; aliquota: number }>;
  das: SimplesProjecao | null;
  notes: string[];
}

export interface RegimeSimulatorResult {
  annualRevenue: number;
  period: { startDate: string; endDate: string };
  regimes: RegimeProjection[];
  recommendation: {
    regime: string;
    label: string;
    estimatedTotalTax: number;
    annualSavingsVsSecond: number;
  } | null;
  engineAudit: {
    evaluatedItems: number;
    totalRulesEvaluated: number;
    errors: string[];
  };
}

export function useRegimeSimulator() {
  return useMutation({
    mutationFn: (input: RegimeSimulatorInput) =>
      apiClient.post<RegimeSimulatorResult>('/fiscal/regime-simulator', input),
  });
}